import { css } from "styled-components";

import { CSS_VARIABLES } from "enums/cssVariables";

import type { RuleSet } from "styled-components";

// Set by Layout only when the page displays a customization background image.
const HAS_BACKGROUND_IMAGE_SELECTOR = `[data-has-background-image="true"]`;

// The entity opacity (voteBackgroundOpacity) drives the frame background, blur and border together,
// so a low opacity is truly transparent. The image itself is never faded.
const FRAME_OPACITY = `var(${CSS_VARIABLES.BACKGROUND_OPACITY})`;
const FRAME_BACKGROUND_COLOR = `rgb(250 250 250 / ${FRAME_OPACITY})`;
const FRAME_BLUR_RADIUS = `calc(${FRAME_OPACITY} * 16px)`;
const FRAME_SATURATION = `calc(100% + ${FRAME_OPACITY} * 60%)`;
const FRAME_BLUR = `blur(${FRAME_BLUR_RADIUS}) saturate(${FRAME_SATURATION})`;
const FRAME_BORDER_COLOR = `rgb(255 255 255 / calc(${FRAME_OPACITY} * 55%))`;
export const FRAME_BORDER_RADIUS = "6px";
// Hover tint for options inside a frame: an opaque light grey would look like a white block.
export const FRAME_HOVER_BACKGROUND_COLOR = "rgb(0 0 0 / 6%)";
// Separates stacked blocks inside a frame once their own shadow is removed (e.g. accordions).
export const FRAME_INNER_BORDER_COLOR = "rgb(0 0 0 / 12%)";

// Styles applied only over a background image: without one, the page keeps its original look.
export const onBackgroundImage = <Props extends object>(
    styles: RuleSet<Props>,
) => css<Props>`
    ${HAS_BACKGROUND_IMAGE_SELECTOR} & {
        ${styles}
    }
`;

// Same as onBackgroundImage, for the sx prop of MUI components.
export const onBackgroundImageSx = (styles: Record<string, string>) => ({
    [`${HAS_BACKGROUND_IMAGE_SELECTOR} &`]: styles,
});

// Frosted frame grouping a page's content over the background image, which may be dark or busy.
// Layout (padding, width...) goes in frameLayout: the conditional selector outranks rules declared outside it.
export const backgroundFrame = (frameLayout?: RuleSet<object>) =>
    onBackgroundImage(css`
        /* Keeps the blur layer below the content, inside the frame. */
        position: relative;
        isolation: isolate;
        box-sizing: border-box;
        border: 1px solid ${FRAME_BORDER_COLOR};
        border-radius: ${FRAME_BORDER_RADIUS};
        background-color: ${FRAME_BACKGROUND_COLOR};
        /* The blur lives on a pseudo-element: a backdrop-filter on the frame itself would become the containing
           block of its position: fixed descendants, such as a dragged ordered question option, which would jump away. */
        &::before {
            content: "";
            position: absolute;
            inset: 0;
            z-index: -1;
            border-radius: inherit;
            pointer-events: none;
            -webkit-backdrop-filter: ${FRAME_BLUR};
            backdrop-filter: ${FRAME_BLUR};
        }
        ${frameLayout}
    `);

// Home and login form frames: border in the entity color, as the original login form drew it.
export const formFrame = (frameLayout?: RuleSet<object>) =>
    backgroundFrame(css`
        border: 2px solid var(${CSS_VARIABLES.BORDER_FORM_COLOR});
        overflow: hidden;
        ${frameLayout}
    `);

// Opaque white elements inside a frame (inputs, idle buttons): the frame background, with the entity opacity.
// Declare it before "&[disabled]" so the disabled look keeps priority.
export const frameFillBackground = onBackgroundImage(css`
    background: ${FRAME_BACKGROUND_COLOR};
`);

// Grey or white zones inside a frame (vote headers, proxies option, footer controls...): no background of their own.
export const frameTransparentBackground = onBackgroundImage(css`
    background-color: transparent;
`);
